import { Exchange } from 'urql'
import { pipe, tap } from 'wonka'
import * as Sentry from '@sentry/nextjs'
import { createHash } from 'crypto'

/**
 * Generate a hash for the GraphQL query to help with Sentry grouping
 */
function generateQueryHash (query: string): string {
  return createHash('md5').update(query).digest('hex').substring(0, 8)
}

/**
 * Extract operation name from GraphQL query string
 */
function extractOperationName (query: string): string | null {
  const match = query.match(/(?:query|mutation|subscription)\s+([A-Za-z_][A-Za-z0-9_]*)/i)
  return match ? match[1] : null
}

/**
 * URQL Exchange that automatically reports all GraphQL errors to Sentry
 * This catches errors that may not be handled by individual hooks
 */
export const sentryExchange: Exchange = ({ forward }) => {
  return operations$ => {
    return pipe(
      operations$,
      forward,
      tap(result => {
        if (result.error) {
          const { error, operation } = result
          const queryString = operation.query.loc?.source?.body || 'Query not available'
          const queryHash = generateQueryHash(queryString)
          const operationName = extractOperationName(queryString)
          const operationKind = operation.kind

          // Don't report expected application errors
          const ignoredErrors = ['NotFoundError', 'ForbiddenError', 'UnauthenticatedError']
          const shouldIgnoreError = (errorMessage: string) => {
            return ignoredErrors.some(ignoredError => errorMessage.includes(ignoredError))
          }

          // Check if this is an ignored error
          if (error.message && shouldIgnoreError(error.message)) {
            return
          }

          // Check GraphQL errors for ignored error types
          if (error.graphQLErrors && error.graphQLErrors.length > 0) {
            const hasIgnoredErrors = error.graphQLErrors.some(gqlError =>
              shouldIgnoreError(gqlError.message)
              || (gqlError.extensions?.code && typeof gqlError.extensions.code === 'string' && ignoredErrors.includes(gqlError.extensions.code)),
            )
            if (hasIgnoredErrors) {
              return
            }
          }

          Sentry.withScope((scope) => {
            // Set transaction name for better organization
            const transactionName = operationName
              ? `GraphQL ${operationKind}: ${operationName}`
              : `GraphQL ${operationKind}: ${queryHash}`

            scope.setTransactionName(transactionName)
            scope.setTag('error_type', 'graphql')
            scope.setTag('query_client', 'urql')
            scope.setTag('operation_kind', operationKind)
            scope.setTag('query_hash', queryHash)

            if (operationName) {
              scope.setTag('operation_name', operationName)
            }

            scope.setContext('graphql_operation', {
              operationName: operationName,
              operationKind: operationKind,
              queryHash: queryHash,
              query: queryString,
              variables: operation.variables,
            })

            // Handle validation errors
            if (error.graphQLErrors && error.graphQLErrors.length > 0) {
              error.graphQLErrors.forEach((gqlError, index) => {
                console.log('URQL Query Error:', gqlError)

                if (gqlError.extensions?.validation) {
                  scope.setTag('error_subtype', 'validation')
                  scope.setLevel(Sentry.Severity.Warning)

                  // Create unique fingerprint for validation errors
                  const validationFields = Object.keys(gqlError.extensions.validation).sort().join(',')

                  scope.setFingerprint([
                    'graphql-validation-error',
                    queryHash,
                    operationKind,
                    validationFields,
                  ])

                  scope.setContext(`validation_error_${index}`, {
                    message: gqlError.message,
                    locations: gqlError.locations,
                    path: gqlError.path,
                    validationFields: gqlError.extensions.validation,
                    extensions: gqlError.extensions,
                  })

                  const validationFieldsCount = Object.keys(gqlError.extensions.validation).length
                  const validationSummary = Object.entries(gqlError.extensions.validation)
                    .map(([field, messages]) => `${field}: ${Array.isArray(messages) ? messages[0] : messages}`)
                    .join(', ')

                  return Sentry.captureException(new Error(`GraphQL Validation Error in ${transactionName}: ${validationFieldsCount} field(s) failed validation - ${validationSummary}`))
                }

                // Create unique fingerprint for other GraphQL errors
                const errorPath = gqlError.path ? gqlError.path.join('.') : 'unknown'

                scope.setFingerprint([
                  'graphql-error',
                  queryHash,
                  operationKind,
                  errorPath,
                  gqlError.message,
                ])

                // Report other errors
                scope.setContext(`graphql_error_${index}`, {
                  message: gqlError.message,
                  locations: gqlError.locations,
                  path: gqlError.path,
                  extensions: gqlError.extensions,
                })

                Sentry.captureException(new Error(`GraphQL Error in ${transactionName}: ${gqlError.message}`))
              })
            }

            // Handle network errors
            if (error.networkError) {
              scope.setTag('error_subtype', 'network')
              scope.setLevel(Sentry.Severity.Error)

              // Create unique fingerprint for network errors
              scope.setFingerprint([
                'graphql-network-error',
                queryHash,
                operationKind,
                error.networkError.message,
              ])

              scope.setContext('network_error', {
                message: error.networkError.message,
                stack: error.networkError.stack,
              })

              Sentry.captureException(new Error(`GraphQL Network Error in ${transactionName}: ${error.networkError.message}`))
            }

            // If it's a combined error, capture it as well
            if (!error.graphQLErrors?.length && !error.networkError) {
              scope.setFingerprint([
                'graphql-combined-error',
                queryHash,
                operationKind,
                error.message,
              ])

              Sentry.captureException(new Error(`GraphQL Combined Error in ${transactionName}: ${error.message}`))
            }
          })
        }
      }),
    )
  }
}
